import React, { useState } from "react";
// ai-bg images not present; use existing assets as fallbacks
import attendanceImage from "../assets/images/ai.jpg";
import inventoryImage from "../assets/images/aw.webp";
import deliveryImage from "../assets/images/blog-hero.webp";

// Import dashboard images (add your own images)
import employeeDashboardImg from "../assets/images/employee.png";
import adminDashboardImg from "../assets/images/admin.jpg";
import hrDashboardImg from "../assets/images/hr.png";

const FeatureSection = () => {
  const [activeTab, setActiveTab] = useState("employee");

  const tabs = [
    {
      id: "employee",
      label: "Employee Dashboard",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      image: employeeDashboardImg,
      title: "Employee Dashboard",
      description: "Personal workspace for employees to manage daily tasks, track attendance, and view payslips.",
      features: [
        "✓ View personal attendance records",
        "✓ Apply for leaves & track status",
        "✓ Download monthly payslips",
        "✓ Submit expense reports",
        "✓ View performance reviews",
        "✓ Update personal information",
        "✓ Access company announcements",
        "✓ Request time-off & holidays"
      ],
      stats: [
        { label: "Present Today", value: "85%", color: "green" },
        { label: "Leaves Available", value: "12", color: "blue" },
        { label: "Pending Tasks", value: "3", color: "orange" }
      ]
    },
    {
      id: "admin",
      label: "Admin Dashboard",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      image: adminDashboardImg,
      title: "Admin Dashboard",
      description: "Complete system control for administrators to manage users, roles, and system configurations.",
      features: [
        "✓ Manage all employees & departments",
        "✓ Configure system settings & roles",
        "✓ Generate company-wide reports",
        "✓ Monitor system performance",
        "✓ Manage user permissions & access",
        "✓ View audit logs & security",
        "✓ Configure payroll settings",
        "✓ Manage company policies"
      ],
      stats: [
        { label: "Total Employees", value: "1,284", color: "green" },
        { label: "Active Departments", value: "12", color: "blue" },
        { label: "System Uptime", value: "99.9%", color: "orange" }
      ]
    },
    {
      id: "hr",
      label: "HR Dashboard",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      image: hrDashboardImg,
      title: "HR Dashboard",
      description: "Comprehensive HR tools for recruitment, onboarding, attendance, and employee management.",
      features: [
        "✓ Manage recruitment & hiring",
        "✓ Track attendance & leaves",
        "✓ Process monthly payroll",
        "✓ Conduct performance reviews",
        "✓ Manage employee benefits",
        "✓ Handle employee grievances",
        "✓ Training & development tracking",
        "✓ Generate HR analytics reports"
      ],
      stats: [
        { label: "Open Positions", value: "8", color: "green" },
        { label: "Pending Approvals", value: "15", color: "blue" },
        { label: "New Joiners", value: "6", color: "orange" }
      ]
    }
  ];

  const currentTab = tabs.find(tab => tab.id === activeTab);

  return (
    <section className="bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* Header Section */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-extrabold text-blue-600 mb-4">
            Employee Management Dashboard
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Complete solution for Employee, Admin, and HR management - all in one place
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-300
                ${activeTab === tab.id 
                  ? "bg-blue-600 text-white shadow-lg transform scale-105" 
                  : "bg-white text-gray-700 hover:bg-blue-50 border border-gray-200"
                }
              `}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard Content */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-12">
          <div className="grid lg:grid-cols-2 gap-0">
            
            {/* Left Column - Image */}
            <div className="relative bg-gradient-to-br from-indigo-50 to-blue-50 p-6 flex items-center justify-center">
              <div className="relative">
                <img
                  src={currentTab.image}
                  alt={currentTab.title}
                  className="rounded-xl shadow-2xl w-full h-auto object-cover"
                  style={{ maxHeight: "500px" }}
                />
                {/* Floating stats overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl"></div>
              </div>
            </div>

            {/* Right Column - Content */}
            <div className="p-8">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  {currentTab.title}
                </h3>
                <p className="text-gray-600">
                  {currentTab.description}
                </p>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {currentTab.stats.map((stat, idx) => (
                  <div key={idx} className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                    <p className={`text-xl font-bold text-${stat.color}-600`}>{stat.value}</p>
                  </div>
                ))}
              </div>

              {/* Features List */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-3">Key Features:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {currentTab.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Button */}
              <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105">
                Access {currentTab.title} →
              </button>
            </div>
          </div>
        </div>

        {/* AI Solutions Section */}
        

        {/* Comparison Section */}
        

        {/* Bottom Features */}
        
      </div>
    </section>
  );
};

export default FeatureSection;